import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { Attribute, Product, ProductSaveResult } from '@nucleus-commerce/api-client/admin';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { toProductUpdate } from '../api/mappers';
import { syncBasePrice } from '../api/option-sync-preview';
import type { ProductFormOutput } from '../schemas/product-form-schema';

import type { ProductSubmitContext } from './use-product-editor';
import { useUpdateProduct } from './use-update-product';
import { variantsQueryOptions } from './use-variants';

export interface UseSaveProductOptions {
  /** After a successful save. */
  onSaved?: (product: Product) => void;
}

/**
 * Saves the master product form. Options go only when they changed, and then carry the variants
 * with them (`variantSync`), at the first variant's price, with a toast when that created or
 * archived any: a save of something else must never touch variants. Resolves with the saved
 * product.
 */
export function useSaveProduct(productId: string, { onSaved }: UseSaveProductOptions = {}) {
  const { t } = useTranslation('products');
  const queryClient = useQueryClient();
  const update = useUpdateProduct(productId);

  const onSubmit = async (
    values: ProductFormOutput,
    attributes: readonly Attribute[],
    { optionsDirty }: ProductSubmitContext = { optionsDirty: false },
  ): Promise<ProductSaveResult> => {
    // Not from a render: a save pressed while the list loads must still know the price.
    const optionSync = optionsDirty
      ? {
          basePrice: syncBasePrice(
            await queryClient.query({ ...variantsQueryOptions(productId), staleTime: 'static' }),
          ),
        }
      : null;
    const product = await update.mutateAsync(toProductUpdate(values, attributes, optionSync));
    const created = product.variantSync?.created ?? 0;
    const archived = product.variantSync?.archived ?? 0;
    if (created > 0 && archived > 0) {
      toast.success(
        `${t('optionsCard.toast.created', { count: created })}, ${t('optionsCard.toast.andArchived', { count: archived })}`,
      );
    } else if (created > 0) {
      toast.success(t('optionsCard.toast.created', { count: created }));
    } else if (archived > 0) {
      toast.success(t('optionsCard.toast.archived', { count: archived }));
    }
    onSaved?.(product);
    return product;
  };

  return { onSubmit, isPending: update.isPending };
}
